import { faMemo } from "@fortawesome/pro-solid-svg-icons";
import { Tooltip } from "@growtherapy/sprout-ui";
import { useAtomValue, useSetAtom } from "jotai";
import { useState } from "react";
import { ControlPanelButton } from "../../components";
import { DrawerButton } from "../../components/DrawerButton";
import { useClientMood } from "../../hooks/measures/useClientMood";
import { useTrackEvent } from "../../segment/segment";
import { EventTypeEnum } from "../../segment/types";
import { participantCountAtom } from "../../video-sdk";
import { useToggleClientInformationDrawerCallback } from "../client-information";
import { DRAWER_STATES_CONTROLLED_BY_CLIENT_INFO_BUTTON } from "../client-information/constants";
import {
  hasNewFormsAtomFamily,
  inSessionClientHasPendingFormsAtom,
} from "../client-information/state";
import { PUSH_DRAWER_ARIA_CONTROLS_ID } from "../constants";
import {
  ProviderDrawerState,
  drawerStateAtom,
  inSessionPatientInformationAtom,
} from "../state";
import { getIsClientInfoDrawerOpen } from "../utils";

export function ClientInformationDrawerControlButton({
  drawerId,
  drawerState,
  onClientInformationButtonClick,
  showVisualIndicator,
}: {
  drawerId: string;
  drawerState: ProviderDrawerState | null;
  onClientInformationButtonClick: () => void;
  showVisualIndicator?: boolean;
}) {
  const isClientInfoDrawerOpen = getIsClientInfoDrawerOpen(drawerState);
  const ariaLabel = isClientInfoDrawerOpen
    ? "Close client information"
    : "View client information";
  const tooltip = isClientInfoDrawerOpen
    ? "Close client information"
    : "Open client information";
  return (
    <div className="hidden md:inline-block">
      <Tooltip text={tooltip} childIsInteractive>
        <DrawerButton
          as={ControlPanelButton}
          // NOTE: This list overlaps with the drawer states controlled by the
          // `ClientsDrawerButton`. This is intentional and ensures that when this
          // button is not available because a provider is not in session, that
          // button can still be focused on drawer close instead.
          // NOTE: Technically a provider can have client info open for a
          // different client than the one they are currently in a session with,
          // in which case it doesn't necessarily make sense to focus this button
          // on closing the drawer, since this button is implied to mean _for the
          // client in the session_. But the focus transference is best-effort,
          // and this is a case where doing it "right" probably isn't worth the
          // added complexity.
          forDrawerStates={DRAWER_STATES_CONTROLLED_BY_CLIENT_INFO_BUTTON}
          containerClassName="hidden md:block"
          aria-label={ariaLabel}
          aria-expanded={isClientInfoDrawerOpen}
          aria-controls={drawerId}
          iconDefinition={faMemo}
          onClick={onClientInformationButtonClick}
          showVisualIndicator={showVisualIndicator}
        />
      </Tooltip>
    </div>
  );
}

export function ClientInformationDrawerControlButtonWrapper() {
  const toggleClientInformationDrawer =
    useToggleClientInformationDrawerCallback();
  const inSessionClientInformation = useAtomValue(
    inSessionPatientInformationAtom,
  );

  const { userClicked } = useTrackEvent();
  const setDrawerState = useSetAtom(drawerStateAtom);
  const drawerState = useAtomValue(drawerStateAtom);
  const clientInfoDrawerIsOpen = getIsClientInfoDrawerOpen(drawerState);
  const isInSession = useAtomValue(participantCountAtom) > 0;
  const hasInSessionClientInformation = useAtomValue(
    inSessionClientHasPendingFormsAtom,
  );
  const hasNewForms = useAtomValue(
    hasNewFormsAtomFamily(inSessionClientInformation?.patientShortId ?? ""),
  );
  const hasRecordedMeasure = !!useClientMood(
    inSessionClientInformation?.visitorUuid,
  );
  const [hasOpenedDrawer, setHasOpenedDrawer] = useState(false);
  const showVisualIndicator =
    !hasOpenedDrawer &&
    (hasInSessionClientInformation || hasNewForms || hasRecordedMeasure);

  if (!isInSession || !inSessionClientInformation?.visitorUuid) {
    return;
  }

  const onClientInformationButtonClick = () => {
    toggleClientInformationDrawer({
      visitorUuid: inSessionClientInformation.visitorUuid,
      patientShortId: inSessionClientInformation.patientShortId,
    });
    setHasOpenedDrawer(true);
    userClicked(EventTypeEnum.CLIENT_INFO, {
      patientShortId: inSessionClientInformation.patientShortId,
    });
  };

  return (
    <ClientInformationDrawerControlButton
      drawerId={PUSH_DRAWER_ARIA_CONTROLS_ID}
      drawerState={drawerState}
      showVisualIndicator={showVisualIndicator}
      onClientInformationButtonClick={
        clientInfoDrawerIsOpen
          ? () => setDrawerState(null)
          : () => onClientInformationButtonClick()
      }
    />
  );
}
